import cs from 'classnames';
import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { RegisterOptions, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { RiCloseFill } from 'react-icons/ri';

import { useFetchAutocompletePlaceMutation } from '@/api/hooks/useRestaurant';
import { PlaceResponse } from '@/api/lib/restaurant';
import { useDebouncedCallback } from '@/hooks/useDebouncedCallback';

import InputGroup from '../../core/InputGroup';
import InputGroupHorizontal from '../../core/InputGroupHorizontal';
import { Spinner } from '@/components/atoms/Spinner';
import { FontWeight } from '@/components/atoms/Typography/interface';
import { AutoCompleteList } from '@/components/molecules/AutoCompleteList';
import { FlattenedSuggestion } from '@/components/molecules/AutoCompleteList/utils';

import { createPlaceSuggestions } from './utils';

type Props = {
  horizontal?: boolean;
  label?: string;
  searchInputId: string;
  searchPlaceId: string;
  placeholder?: string;
  helperText?: string;
  required?: boolean;
  type?: 'text';
  readOnly?: boolean;
  disabled?: boolean;
  inputOptions?: RegisterOptions;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  defaultValue?: string;
  tooltipMessage?: string;
  weight?: FontWeight;
  match?: string;
  onSelectSuggestion?: (suggestion: FlattenedSuggestion, restaurantId: string | null) => void;
};

export type AutoCompleteRestaurantHandle = {
  resetRestaurant: () => void;
};

export const AutoCompleteRestaurantInput = forwardRef<AutoCompleteRestaurantHandle, Props>((props, ref) => {
  const {
    horizontal,
    label,
    placeholder = '',
    helperText,
    defaultValue,
    searchInputId,
    searchPlaceId,
    type = 'text',
    readOnly = false,
    inputOptions,
    leftIcon,
    disabled = false,
    required = false,
    rightIcon,
    tooltipMessage,
    weight,
    onSelectSuggestion
  } = props;
  const { t } = useTranslation();
  const {
    register,
    formState: { errors },
    watch,
    setValue
  } = useFormContext();
  const { onChange: registerOnChange, ...registerProps } = register(searchInputId, inputOptions);
  const formError = errors[searchPlaceId];
  const Group = horizontal ? InputGroupHorizontal : InputGroup;
  const restaurantInput = watch(searchInputId);

  const [placeData, setPlaceData] = useState<PlaceResponse[]>([]);

  const { isLoading, mutate: fetchAutocompletePlaceMutation } = useFetchAutocompletePlaceMutation({
    onSuccess: (data) => {
      setPlaceData(data);
    }
  });

  const debounceAutoCompleteRestaurantChange = useDebouncedCallback((restaurant: string) => {
    if (restaurant && restaurant.length >= 2) {
      fetchAutocompletePlaceMutation({
        search: restaurant
      });
    } else {
      setPlaceData([]);
    }
  }, 200);

  const handleSelectRestaurantSuggestion = (suggestion: FlattenedSuggestion) => {
    setValue(searchInputId, suggestion.text);
    setValue(searchPlaceId, suggestion.id);
    setPlaceData([]);
    if (onSelectSuggestion) {
      const foundPlace = placeData.find((place) => place.placeId === suggestion.id);
      onSelectSuggestion(suggestion, foundPlace?.restaurantId || null);
    }
  };

  const handleClearInputRestaurant = () => {
    setPlaceData([]);
    setValue(searchInputId, '');
    setValue(searchPlaceId, '');
  };

  useImperativeHandle(ref, () => ({
    resetRestaurant() {
      handleClearInputRestaurant();
    }
  }));

  return (
    <Group
      id={searchInputId}
      required={required}
      label={label}
      helperText={helperText}
      error={formError?.message as unknown as string | undefined}
      center
      tooltipMessage={tooltipMessage}
      weight={weight}
    >
      {isLoading && (
        <div className="absolute top-1 right-2">
          <Spinner reverse={true} />
        </div>
      )}
      {restaurantInput && !isLoading && !disabled && (
        <RiCloseFill size={20} color="black" className="absolute top-2 right-2" onClick={() => handleClearInputRestaurant()} />
      )}
      <div className="flex flex-row">
        <input
          data-testid={`input-${searchInputId}`}
          type={type}
          id={searchInputId}
          disabled={disabled}
          autoComplete="one-time-code"
          readOnly={readOnly}
          className={cs('error', [leftIcon && 'pl-10', rightIcon && 'pr-10'], 'p-2 rounded-md')}
          placeholder={placeholder}
          aria-describedby={searchInputId}
          defaultValue={defaultValue}
          onChange={(event) => {
            debounceAutoCompleteRestaurantChange(event.target.value);
            registerOnChange(event);
          }}
          {...registerProps}
        />
      </div>
      {placeData && placeData.length > 0 && (
        <AutoCompleteList
          suggestions={createPlaceSuggestions(t, placeData) ?? []}
          match={restaurantInput}
          onSelectSuggestion={handleSelectRestaurantSuggestion}
        />
      )}
    </Group>
  );
});
